import React, { useState, useEffect, useCallback } from 'react';
import { dashboardAPI } from '../services/api';
import DateFilter from '../components/DateFilter';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const fmt = (v) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(v);
const PIE_COLORS = ['#2563eb', '#f59e0b'];

export default function CarRentalsPage() {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async (params = {}, p = 1) => {
    setLoading(true);
    try {
      const res = await dashboardAPI.carrentals({ ...params, page: p, limit: 20 });
      setData(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  const handleFilter = (params) => { setFilters(params); setPage(1); load(params, 1); };
  const handlePage = (p) => { setPage(p); load(filters, p); };

  if (loading && !data) return <div className="loading">Yükleniyor...</div>;

  const filtered = data?.data?.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return [r.yolcu_adi, r.yolcu_soyadi, r.teslim_sehri, r.birakma_sehri, r.islem_no].some(v => v && v.toLowerCase().includes(s));
  });

  const totalAmount = data?.totalAmount || data?.data?.reduce((s, r) => s + (r.satis_toplam || 0), 0) || 0;
  const uniquePassengers = data?.uniquePassengers || new Set(data?.data?.map(r => (r.yolcu_adi || '') + ' ' + (r.yolcu_soyadi || '')).filter(Boolean)).size;

  return (
    <div>
      <div className="topbar">
        <h1>Araç Kiralama Analitik</h1>
        <DateFilter onFilter={handleFilter} />
      </div>

      {data && (
        <>
          {/* Stats */}
          <div className="stats-grid" style={{ marginBottom: 16 }}>
            <div className="stat-card">
              <div className="label">Toplam Kiralama</div>
              <div className="value">{data.total || 0}</div>
            </div>
            <div className="stat-card">
              <div className="label">Toplam Tutar</div>
              <div className="value">{fmt(totalAmount)}</div>
            </div>
            <div className="stat-card">
              <div className="label">Farklı Yolcu</div>
              <div className="value">{uniquePassengers}</div>
            </div>
            <div className="stat-card">
              <div className="label">Ort. Kiralama Tutarı</div>
              <div className="value">{fmt(data.total ? totalAmount / data.total : 0)}</div>
            </div>
          </div>

          {/* Charts Row: City Bar + Yurtiçi/Yurtdışı Pie */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div className="chart-card">
              <h3>Şehir Dağılımı</h3>
              <ResponsiveContainer width="100%" height={Math.max(200, (data.cities?.length || 3) * 40)}>
                <BarChart data={data.cities?.slice(0, 10)} layout="vertical" margin={{ left: 120 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="city" tick={{ fontSize: 11 }} width={110} />
                  <Tooltip formatter={(v, name) => name === 'total' ? fmt(v) : v} />
                  <Bar dataKey="count" name="Adet" fill="#dc2626" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-card">
              <h3>Yurtiçi / Yurtdışı Dağılımı</h3>
              {data.hatTipi && data.hatTipi.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie data={data.hatTipi} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={85}
                        label={({ name, percent }) => `${name} %${(percent * 100).toFixed(0)}`}
                        labelLine={{ strokeWidth: 1 }}>
                        {data.hatTipi.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v, name, entry) => [`${v} kiralama`, entry.payload.name]} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ display: 'flex', gap: 24, marginTop: 8, fontSize: 13 }}>
                    {data.hatTipi.map((h, i) => (
                      <div key={i} style={{ textAlign: 'center' }}>
                        <div style={{ fontWeight: 600, color: PIE_COLORS[i] }}>{h.name}</div>
                        <div>{h.count} kiralama</div>
                        <div style={{ fontWeight: 600 }}>{fmt(h.total)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Veri yok</div>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="chart-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <h3>Kiralama Detayları ({data.total} kayıt)</h3>
              <input type="text" placeholder="Ara..." value={search} onChange={e => setSearch(e.target.value)}
                style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, width: 220 }} />
            </div>
            <div style={{ overflowX: 'auto', maxHeight: 400, overflowY: 'auto' }}>
              <table className="data-table">
                <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                  <tr>
                    <th>Yolcu</th>
                    <th>İşlem No</th>
                    <th>İşlem Tarihi</th>
                    <th>Teslim Şehri</th>
                    <th>Bırakma Şehri</th>
                    <th>Yurtiçi/Dışı</th>
                    <th>Tutar</th>
                    <th>Talep Eden</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered?.map(r => (
                    <tr key={r.id}>
                      <td>{r.yolcu_adi} {r.yolcu_soyadi}</td>
                      <td style={{ fontSize: 11, color: '#6b7280' }}>{r.islem_no}</td>
                      <td>{r.islem_tarihi?.slice(0, 10)}</td>
                      <td>{r.teslim_sehri}</td>
                      <td>{r.birakma_sehri}</td>
                      <td>{r.islem_yeri || '-'}</td>
                      <td style={{ fontWeight: 600 }}>{fmt(r.satis_toplam)}</td>
                      <td>{r.talep_eden}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.totalPages > 1 && (
              <div className="pagination">
                <button disabled={page <= 1} onClick={() => handlePage(page - 1)}>← Önceki</button>
                <span style={{ fontSize: 13, color: '#6b7280' }}>{page} / {data.totalPages}</span>
                <button disabled={page >= data.totalPages} onClick={() => handlePage(page + 1)}>Sonraki →</button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
